import React, { Fragment, useContext, useState } from "react";
import { omit } from "lodash";
import { useDropzone } from "react-dropzone";
import { FormProvider, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { UserRoleNames } from "common/authorization/policies";
import { MIME_TYPE } from "common/constants";
import { useDispatchBulkUserInviteScriptJobMutation } from "../../generated/graphql";
import { useMultipartFileUpload } from "../../hooks/useMultipartFileUpload";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { AuthContext } from "../Authorization/AuthContext";
import {
  Container,
  FormContainer,
  Section,
} from "../Common/__styles__/FullPageFormLayout";
import { Button } from "../Common/Button";
import FullPageFormLayout from "../Common/FullPageFormLayout";
import { Body } from "../Common/Typography";
import { SingleFileUpload } from "../Inputs/react-hook-form";
import { List, ListItem } from "./Data/Operations/__styles__/Operations";

type BulkUserInviteFormData = {
  fileUpload: { blob: File };
};

const RoleCode = ({
  role,
  isLastRole,
}: {
  role: string;
  isLastRole: boolean;
}) => {
  return (
    <Fragment>
      <code>{role}</code>
      {isLastRole ? "" : ", "}
    </Fragment>
  );
};

const BulkUserInviteForm = () => {
  const history = useHistory();
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();
  const formMethods = useForm<BulkUserInviteFormData>();
  const { control, getValues, handleSubmit, register } = formMethods;
  const [hasDispatchedOperation, setHasDispatchedOperation] = useState(false);
  const prevLocation = "/settings/users";
  const isSSOEnabled = account?.isSSOEnabled ?? false;

  const [uploadSourceFile, { loading: isUploading }] = useMultipartFileUpload({
    onError: () => addErrorToast("Failed to upload input file"),
  });

  const [dispatch, { loading: isDispatching }] =
    useDispatchBulkUserInviteScriptJobMutation({
      onCompleted: () => {
        addSuccessToast(
          "Bulk user invite job dispatched! You will receive an email with the results.",
        );
        setHasDispatchedOperation(true);
        history.push(prevLocation);
      },
      onError: () => {
        addErrorToast("Failed to dispatch bulk user invite job");
      },
    });

  const onSubmit = async () => {
    uploadSourceFile({
      file: getValues("fileUpload.blob"),
      onCompleted: async key => {
        await dispatch({ variables: { data: { inputCsvKey: key } } });
      },
    });
  };

  const isSubmitting = isUploading || isDispatching;
  const submitIsDisabled = isSubmitting || hasDispatchedOperation;

  return (
    <FullPageFormLayout
      subtitle="Bulk User Invite"
      prevLocation={prevLocation}
      rightContainer={
        <Button
          size={"small"}
          styleVariant={"primary"}
          onClick={handleSubmit(onSubmit)}
          disabled={submitIsDisabled}
          loading={isSubmitting}
        >
          Submit
        </Button>
      }
    >
      <Container>
        <FormContainer>
          <FormProvider {...formMethods}>
            <form>
              <Section style={{ marginTop: "16px" }}>
                <Body
                  size={"default"}
                  type={"regular"}
                  color="contentSecondary"
                >
                  This form allows you to upload a CSV file to bulk invite users
                  to the platform. When the job is complete, you will receive an
                  email with the results.
                </Body>
              </Section>
              <Section>
                <List>
                  <ListItem>
                    <Body
                      type="regular"
                      size="default"
                      color="contentSecondary"
                    >
                      The first row of your CSV should contain the column
                      headers
                    </Body>
                  </ListItem>
                  <ListItem>
                    <Body
                      type="regular"
                      size="default"
                      color="contentSecondary"
                      as="span"
                    >
                      Required columns: <code>firstName</code>,{" "}
                      <code>lastName</code>, <code>email</code>,{" "}
                      <code>role</code>
                    </Body>
                  </ListItem>
                  <ListItem>
                    <Body
                      type="regular"
                      size="default"
                      color="contentSecondary"
                      as="span"
                    >
                      Optional columns:{" "}
                      {isSSOEnabled && (
                        <>
                          <code>authMethod</code> (defaults to{" "}
                          <code>password</code>),{" "}
                        </>
                      )}
                      <code>canViewPersonalIdentifiableInformation</code>{" "}
                      (defaults to <code>false</code>)
                    </Body>
                  </ListItem>
                  {isSSOEnabled && (
                    <ListItem>
                      <Body
                        type="regular"
                        size="default"
                        color="contentSecondary"
                        as="span"
                      >
                        Valid authMethod values: <code>sso</code>,{" "}
                        <code>password</code>
                      </Body>
                    </ListItem>
                  )}
                  <ListItem>
                    <Body
                      type="regular"
                      size="default"
                      color="contentSecondary"
                      as="span"
                    >
                      Valid roles:{" "}
                      {UserRoleNames.map((role, index) => (
                        <RoleCode
                          key={role}
                          role={role}
                          isLastRole={index === UserRoleNames.length - 1}
                        />
                      ))}
                    </Body>
                  </ListItem>
                  <ListItem>
                    <Body
                      type="regular"
                      size="default"
                      color="contentSecondary"
                      as="span"
                    >
                      <strong>Important:</strong> You can only grant PII access
                      if you have PII permissions yourself. Rows requesting PII
                      access will fail if you don't have this permission.
                    </Body>
                  </ListItem>
                  <ListItem>
                    <Body
                      type="regular"
                      size="default"
                      color="contentSecondary"
                      as="span"
                    >
                      Example CSV format:
                      <pre style={{ marginTop: "8px" }}>
                        {isSSOEnabled ?
                          `firstName,lastName,email,role,authMethod,canViewPersonalIdentifiableInformation
John,Doe,john.doe@example.com,${UserRoleNames[3]},password,false
Jane,Smith,jane.smith@example.com,${UserRoleNames[2]},sso,true`
                        : `firstName,lastName,email,role,canViewPersonalIdentifiableInformation
John,Doe,john.doe@example.com,${UserRoleNames[3]},false
Jane,Smith,jane.smith@example.com,${UserRoleNames[2]},true`
                        }
                      </pre>
                    </Body>
                  </ListItem>
                </List>
              </Section>
              <Section>
                <SingleFileUpload
                  {...omit(register("fileUpload"), "ref")}
                  control={control}
                  allowedMimeTypes={[MIME_TYPE.CSV]}
                  name={"fileUpload"}
                  useDropzone={useDropzone}
                  label={"Source file"}
                  description={"Upload a CSV file"}
                  required
                />
              </Section>
            </form>
          </FormProvider>
        </FormContainer>
      </Container>
    </FullPageFormLayout>
  );
};

export default BulkUserInviteForm;
